/* One viewport pass on the existing native product CTA. No purchase handlers. */
(() => {
  'use strict';
  if (window.swProductCtaShineReady || !('IntersectionObserver' in window)) return;
  window.swProductCtaShineReady = true;

  const selector = '.product-detail button.sw-v2-cta[data-add-to-cart]';
  const effectClass = 'sw-v2-cta--exposed';
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const seen = new WeakSet();
  const controllers = new Map();

  function eligible(button, root) {
    if (motion.matches || document.hidden || !root.isConnected || !button.isConnected ||
        !root.contains(button) || button.disabled || button.getAttribute('aria-disabled') === 'true' ||
        button.closest('.variant--unavailable, .add-in-progress') || !button.getClientRects().length) return false;
    for (let node = button; node; node = node.parentElement) {
      const style = getComputedStyle(node);
      if (node.hidden || style.display === 'none' || style.visibility === 'hidden' ||
          style.visibility === 'collapse' || Number(style.opacity) === 0) return false;
    }
    const rect = button.getBoundingClientRect();
    const width = Math.max(0, Math.min(rect.right, document.documentElement.clientWidth) - Math.max(rect.left, 0));
    const height = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
    return rect.width > 0 && rect.height > 0 && width * height / (rect.width * rect.height) >= 0.5;
  }

  function mount(root) {
    if (controllers.has(root)) return;
    const states = new Map();
    let stopped = false;

    function cancel(state) {
      clearTimeout(state.timer);
      cancelAnimationFrame(state.frame);
      state.timer = state.frame = 0;
    }

    function consider(button, state) {
      if (stopped || seen.has(button)) return;
      if (!state.visible || !eligible(button, root)) { cancel(state); return; }
      if (state.timer || state.frame) return;
      state.timer = setTimeout(() => {
        state.timer = 0;
        state.frame = requestAnimationFrame(() => {
          state.frame = 0;
          if (stopped || !state.visible || seen.has(button) || !eligible(button, root)) return;
          seen.add(button);
          button.classList.add(effectClass);
          observer.unobserve(button);
          state.cleanup = setTimeout(() => { button.classList.remove(effectClass); state.cleanup = 0; }, 850);
          if ([...states.keys()].every(item => seen.has(item))) {
            observer.disconnect();
            changes.disconnect();
          }
        });
      }, 120);
    }

    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        const state = states.get(entry.target);
        if (!state) return;
        state.visible = entry.isIntersecting && entry.intersectionRatio >= 0.5;
        consider(entry.target, state);
      });
    }, { threshold: [0, 0.5] });

    function refresh() {
      states.forEach((state, button) => {
        if (!button.isConnected || !root.contains(button)) {
          cancel(state);
          clearTimeout(state.cleanup);
          button.classList.remove(effectClass);
          observer.unobserve(button);
          states.delete(button);
        }
      });
      root.querySelectorAll(selector).forEach(button => {
        if (seen.has(button) || states.has(button)) return;
        states.set(button, { visible: false, timer: 0, frame: 0, cleanup: 0 });
        observer.observe(button);
      });
      states.forEach((state, button) => consider(button, state));
    }

    const changes = new MutationObserver(refresh);
    changes.observe(root, { subtree: true, childList: true, attributes: true,
      attributeFilter: ['disabled', 'aria-disabled', 'hidden', 'class', 'style'] });
    controllers.set(root, {
      refresh,
      stop() {
        stopped = true;
        observer.disconnect();
        changes.disconnect();
        states.forEach((state, button) => {
          cancel(state);
          clearTimeout(state.cleanup);
          button.classList.remove(effectClass);
        });
        states.clear();
        controllers.delete(root);
      }
    });
    refresh();
  }

  function init(scope) {
    if (scope.matches?.('[data-section-type="product"]')) mount(scope);
    scope.querySelectorAll('[data-section-type="product"]').forEach(mount);
  }
  function refreshAll() { controllers.forEach(controller => controller.refresh()); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    controllers.forEach((controller, root) => {
      if (event.target === root || event.target.contains(root)) controller.stop();
    });
  });
  document.addEventListener('visibilitychange', refreshAll);
  motion.addEventListener('change', refreshAll);
})();
